<template>
  <div>
    <div
      v-for="(payableSection, sectionIndex) in sortedSections"
      :key="sectionIndex"
      class="order-content"
      data-test="payableSection"
    >
      <v-card
        v-if="$vuetify.breakpoint.mdAndUp"
        color="primary"
        class="fipa--header white--text"
        :class="{
          'fipa--header-registration':
            payableSection.entityType == 'Registration'
        }"
        tile
      >
        <div
          class="d-flex w-100"
          :class="{
            'full-height': payableSection.entityType != 'Invoice'
          }"
          style="width: 100%"
        >
          <div v-if="payableSection.entityType == 'Invoice'" class="px-2">
            <b class="white--text">{{
              $t(
                "people:shoppingBasket.shoppingBasketTab.invoiceComponent.invoicedItems.subheading",
                "Invoiced items"
              )
            }}</b>
          </div>
          <div
            v-else
            :class="{
              'payable-section-checkbox-trickery':
                payableSection.entityType != 'Registration'
            }"
          >
            <v-checkbox
              :input-value="payableSectionSelected"
              color="accent"
              :value="payableSection.id"
              dark
              data-test="sectionCheckbox"
              multiple
              :disabled="sectionDisabled(payableSection, sectionIndex)"
              hide-details
              class="mt-0 checkbox-white-background"
              @change="
                payableSectionIds =>
                  updateSectionSelection(payableSectionIds, payableSection)
              "
            >
              <template #label>
                <b class="white--text">{{ sectionTitle(payableSection) }}</b>
              </template>
            </v-checkbox>
          </div>
          <div
            v-if="payableSection.entityType == 'Registration'"
            class="d-flex ml-auto"
            style="align-items: center"
          >
            <div class="pl-6 d-flex">
              <v-btn
                v-if="
                  $allowedMutation(
                    payableSection.registration.allowedMutations,
                    'registrationChangeRegistrantPerson'
                  )
                "
                icon
                small
                :aria-label="
                  $t(
                    'people:ShoppingBasket.ChangeRegistrationResponsible.button.alt',
                    'Change registrant'
                  )
                "
                class="edit add-btn"
                @click="() => openPayerDialog(payableSection.registration)"
              >
                <v-icon color="white" small>$iconUser</v-icon>
              </v-btn>
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    :aria-label="
                      $t(
                        'people:ShoppingBasket.Payable.editRegistrationButton.alt'
                      )
                    "
                    class="edit-green add-btn"
                    :to="{
                      name: 'EventRegistrationEditPage',
                      params: {
                        eventId: payableSection.registration.eventId,
                        registrationId: payableSection.registration.id
                      }
                    }"
                    v-on="on"
                  >
                    <v-icon color="white" small>$edit</v-icon>
                  </v-btn>
                </template>
                <span>
                  {{
                    $t(
                      "people:ShoppingBasket.ShoppingBasketTab.EditRegistration.label",
                      "Edit registration",
                      "Label of the order table section"
                    )
                  }}
                </span>
              </v-tooltip>
            </div>
          </div>
          <div
            v-else-if="payableSection.entityType == 'Event'"
            class="d-flex ml-auto"
            style="align-items: center"
          >
            <v-btn
              v-if="
                $allowedMutation(
                  'eventCancelPayableFromOrganiser',
                  returnPayableItemMutations(payableSection)
                )
              "
              icon
              small
              class="remove d-flex"
              data-test="eventDeleteItem"
              @click="$event => cancelWalletFunds(payableSection, $event)"
            >
              <v-icon small color="white">$iconTrash</v-icon>
              <v-progress-circular
                v-show="false"
                color="white"
                size="21"
                indeterminate
              />
            </v-btn>
          </div>
        </div>

        <template v-if="payableSection.entityType == 'Registration'">
          <v-divider
            class="my-2 w-100"
            dark
            style="height: 1px; width: 100%; border-color: white"
          />

          <div class="w-100 mr-auto">
            <div>
              <div class="d-flex">
                <div class="mb-1" style="min-width: 80px">
                  <b>
                    {{
                      $t(
                        "people:ShoppingBasketPage.ShoppingBasketTab.Athlete.label",
                        "Athlete",
                        "Label of the order table section"
                      )
                    }}:
                  </b>
                </div>

                <div class="d-flex flex-wrap">
                  <AppShowAthleteName
                    v-for="athlete in payableSection.registration.athletes"
                    :key="athlete.id"
                    :athlete="athlete"
                    name-only
                    class="mr-4 mb-1 white--text underline-hover"
                  />
                </div>
              </div>
            </div>
            <div v-if="payableSection.registration.horses.length">
              <div class="d-flex">
                <div class="mb-1" style="min-width: 80px">
                  <b>
                    {{
                      $t(
                        "people:ShoppingBasket.ShoppingBasketTab.Horse.label",
                        "Horse",
                        "Label of the order table section"
                      )
                    }}:
                  </b>
                </div>

                <div class="d-flex flex-wrap">
                  <template
                    v-if="
                      payableSection.registration.horses.some(
                        x => x.isHorseSlot
                      )
                    "
                  >
                    <AppHorseSlot />
                  </template>
                  <template v-else>
                    <AppShowHorseName
                      v-for="horse in payableSection.registration.horses"
                      :key="horse.id"
                      :horse="horse"
                      class="mr-4 mb-1 white--text underline-hover"
                    />
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>
      </v-card>

      <v-sheet v-else color="primary" class="white--text px-0 py-2" tile>
        <v-row no-gutters class="px-5 py-2">
          <v-col class="d-flex">
            <div v-if="payableSection.entityType == 'Invoice'" class="px-2">
              <b class="white--text">{{
                $t(
                  "people:shoppingBasket.shoppingBasketTab.invoiceComponent.invoicedItems.subheading",
                  "Invoiced items"
                )
              }}</b>
            </div>
            <v-checkbox
              :input-value="payableSectionSelected"
              color="accent"
              :value="payableSection.id"
              dark
              data-test="sectionCheckbox"
              multiple
              :disabled="sectionDisabled(payableSection, sectionIndex)"
              hide-details
              class="mt-0 checkbox-white-background"
              @change="
                payableSectionIds =>
                  updateSectionSelection(payableSectionIds, payableSection)
              "
            >
              <template
                v-if="payableSection.entityType != 'Registration'"
                #label
              >
                <b class="white--text" style="font-size: 12px">{{
                  sectionTitle(payableSection)
                }}</b>
              </template>
            </v-checkbox>
            <div v-if="payableSection.registration" style="font-size: 12px">
              <div class="mb-2">
                <b class="mr-1">
                  {{
                    $t(
                      "people:ShoppingBasket.ShoppingBasketTab.Athlete.label",
                      "Athlete",
                      "Label of the order table section"
                    )
                  }}:
                </b>
                <div
                  v-for="athlete in payableSection.registration.athletes"
                  :key="athlete.id"
                  class="mb-2"
                >
                  <AppShowAthleteName
                    :athlete="athlete"
                    name-only
                    class-list="white--text"
                  />
                </div>
              </div>

              <div v-if="payableSection.registration.horses.length">
                <b class="mr-1">
                  {{
                    $t(
                      "people:ShoppingBasket.ShoppingBasketTab.Horse.label",
                      "Horse",
                      "Label of the order table section"
                    )
                  }}:
                </b>
                <template
                  v-if="
                    payableSection.registration.horses.some(x => x.isHorseSlot)
                  "
                >
                  <AppHorseSlot />
                </template>
                <template v-else>
                  <div
                    v-for="horse in payableSection.registration.horses"
                    :key="horse.id"
                    class="mb-2"
                  >
                    <AppShowHorseName
                      :horse="horse"
                      class-list="white--text "
                    />
                  </div>
                </template>
              </div>
            </div>
          </v-col>
          <template v-if="payableSection.registration">
            <v-col
              class="d-flex flex-column justify-space-between"
              style="flex-shrink: 1; flex-grow: 0"
            >
              <div
                class="text-right"
                style="
                  display: flex;
                  flex-direction: column;
                  align-items: flex-end;
                "
              >
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      :aria-label="
                        $t(
                          'people:ShoppingBasket.Payable.editRegistrationButton.alt'
                        )
                      "
                      class="edit-green"
                      :to="{
                        name: 'EventRegistrationEditPage',
                        params: {
                          eventId: payableSection.registration.eventId,
                          registrationId: payableSection.registration.id
                        }
                      }"
                      v-on="on"
                    >
                      <v-icon color="white" small>$edit</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    {{
                      $t(
                        "people:ShoppingBasket.ShoppingBasketTab.EditRegistration.label",
                        "Edit registration",
                        "Label of the order table section"
                      )
                    }}
                  </span>
                </v-tooltip>
              </div>
              <div class="text-right"></div>
            </v-col>
          </template>
          <div
            v-else-if="payableSection.entityType == 'Event'"
            class="text-right py-0"
          >
            <v-btn
              v-if="
                $allowedMutation(
                  'eventCancelPayableFromOrganiser',
                  returnPayableItemMutations(payableSection)
                )
              "
              icon
              small
              class="remove d-flex"
              :aria-label="$t('common:deleteButton.alt')"
              data-test="eventDeleteItem"
              @click="$event => cancelWalletFunds(payableSection, $event)"
            >
              <v-icon small color="white">$iconTrash</v-icon>
              <v-progress-circular
                v-show="false"
                color="white"
                size="21"
                indeterminate
              />
            </v-btn>
          </div>
        </v-row>
      </v-sheet>

      <template v-if="payableSection.entityType == 'Invoice'">
        <p class="muted--text px-2 mb-3 mt-4">
          {{
            $t(
              "people:shoppingBasket.shoppingBasketTab.invoiceComponent.invoicedItems.disclaimer",
              "This is a summary of payables in the invoice. Download pdf. for original document."
            )
          }}
        </p>
      </template>

      <table class="entsys--table">
        <colgroup v-if="$vuetify.breakpoint.mdAndDown">
          <col style="width: 1px" />
          <col />
          <col />
        </colgroup>
        <thead v-if="$vuetify.breakpoint.lgAndUp">
          <tr
            v-if="['Event', 'Registration'].includes(payableSection.entityType)"
          >
            <th style="width: 48px"></th>
            <th class="text-left" style="width: 20%">
              {{
                $t(
                  "people:ShoppingBasket.ShoppingBasketTab.Item.label",
                  "Item",
                  "Label of the order table section"
                )
              }}
            </th>
            <th class="text-left" style="width: 45%">
              {{
                $t(
                  "people:ShoppingBasket.ShoppingBasketTab.Details.label",
                  "Details",
                  "Label of the order table section"
                )
              }}
            </th>
            <th class="text-left" style="width: 25%">
              {{
                $t(
                  "people:ShoppingBasket.ShoppingBasketTab.Deadline.label",
                  "Deadline",
                  "Label of the order table section"
                )
              }}
            </th>

            <th class="text-right" style="width: 10%">
              {{
                $t(
                  "people:ShoppingBasket.ShoppingBasketTab.Total.label",
                  "Total",
                  "Label of the order table section"
                )
              }}
            </th>
            <th style="width: 0px" />
          </tr>

          <tr v-else-if="payableSection.entityType == 'Product'">
            <th style="width: 48px"></th>
            <th class="text-left" style="width: 20%">
              {{
                $t(
                  "people:ShoppingBasket.ShoppingBasketTab.Item.label",
                  "Item",
                  "Label of the order table section"
                )
              }}
            </th>
            <th class="text-left" style="width: 65%">
              {{
                $t(
                  "people:ShoppingBasket.ShoppingBasketTab.Details.label",
                  "Details",
                  "Label of the order table section"
                )
              }}
            </th>

            <th
              v-if="showDeadlineColumn(payableSection.payableItems)"
              class="text-left"
              style="width: 25%"
              data-test="paymentDeadlineHeader"
            >
              {{
                $t(
                  "people:ShoppingBasket.ShoppingBasketTab.Deadline.label",
                  "Deadline",
                  "Label of the order table section"
                )
              }}
            </th>

            <th class="text-right" style="width: 10%">
              {{
                $t(
                  "people:ShoppingBasket.ShoppingBasketTab.Total.label",
                  "Total",
                  "Label of the order table section"
                )
              }}
            </th>
            <th style="width: 0px" />
          </tr>

          <tr v-else-if="payableSection.entityType == 'Invoice'">
            <th style="width: 48px"></th>
            <th class="text-left" style="width: 20%">
              {{
                $t(
                  "people:shoppingBasket.shoppingBasketTab.invoiceComponent.item.label",
                  "Item"
                )
              }}
            </th>
            <th class="text-left" style="width: 65%">
              {{
                $t(
                  "people:shoppingBasket.shoppingBasketTab.invoiceComponent.details.label",
                  "Details"
                )
              }}
            </th>
            <th class="text-right" style="width: 10%">
              {{
                $t(
                  "people:shoppingBasket.shoppingBasketTab.invoiceComponent.amount.label",
                  "Amount"
                )
              }}
            </th>
            <th style="width: 0px" />
          </tr>
        </thead>

        <tbody>
          <component
            :is="componentByEntityType(item.entityType)"
            v-for="(item, i) in sortBy(payableSection.payableItems, [
              'entityType',
              'rank'
            ])"
            :key="i"
            :item="item"
            :product="payableSection.product"
            :show-payment-deadline="
              showDeadlineColumn(payableSection.payableItems)
            "
          >
            <template v-if="!payable.singlePaymentForWholeGroup" #selection>
              <template v-if="!item.paymentInProcess">
                <v-checkbox
                  v-if="item.mandatory"
                  :input-value="true"
                  disabled
                  class="mt-0 py-0"
                  data-test="payableItemCheckbox"
                  hide-details
                />
                <v-checkbox
                  v-else-if="!item.paymentInProcess"
                  v-model="payableItemsSelected"
                  color="accent"
                  data-test="payableItemCheckbox"
                  :disabled="cannotSelectItem(item)"
                  :value="item.id"
                  multiple
                  class="mt-0 py-0"
                  hide-details
                  @change="
                    payableItemIds => addRemoveAddons(payableItemIds, item)
                  "
                />
              </template>
              <span v-else class="secondary--text">
                <v-icon>$iconAlarmClock</v-icon>
              </span>
            </template>

            <template #price>
              <b class="accent--text" style="white-space: nowrap">{{
                masterCurrency(item.amountCents)
              }}</b>
            </template>

            <template #notAllowedReasons>
              <div
                v-if="
                  item.paymentNotAllowedReasons &&
                  item.paymentNotAllowedReasons.length > 0
                "
                class="alert pa-2"
              >
                <ul>
                  <li
                    v-for="(
                      reason, reasonIndex
                    ) in item.paymentNotAllowedReasons"
                    :key="reasonIndex"
                  >
                    {{ displayText(reason) }}
                  </li>
                </ul>
              </div>
            </template>
            <template #actions="{ buttonProps }">
              <div
                v-if="
                  $allowedMutation(
                    item.allowedMutations,
                    'eventCancelPayableFromOrganiser'
                  ) ||
                  $allowedMutation(
                    item.payable.allowedMutations,
                    'shoppingBasketCancelPayable'
                  )
                "
              >
                <v-btn
                  v-if="
                    $allowedMutation(
                      item.payable.allowedMutations,
                      'shoppingBasketCancelPayable'
                    )
                  "
                  icon
                  small
                  :disabled="frozen"
                  v-bind="buttonProps"
                  :aria-label="$t('common:deleteButton.alt')"
                  class="remove d-flex"
                  data-test="removeItem"
                  @click="$event => removeItem(item, $event)"
                >
                  <v-icon small color="white">$iconTrash</v-icon>
                  <v-progress-circular
                    v-show="false"
                    color="white"
                    size="21"
                    indeterminate
                  />
                </v-btn>
                <v-btn
                  v-else-if="
                    $allowedMutation(
                      item.payable.allowedMutations,
                      'eventCancelPayableFromOrganiser'
                    )
                  "
                  icon
                  small
                  :disabled="frozen"
                  v-bind="buttonProps"
                  :aria-label="$t('common:deleteButton.alt')"
                  class="remove d-flex"
                  data-test="eventDeleteItem"
                  @click="$event => cancelWalletFund(item.id, $event)"
                >
                  <v-icon small color="white">$iconTrash</v-icon>
                  <v-progress-circular
                    v-show="false"
                    color="white"
                    size="21"
                    indeterminate
                  />
                </v-btn>
              </div>
            </template>
          </component>
        </tbody>
      </table>
    </div>

    <div class="text-right my-4">
      <template v-if="payable.entityType != 'Invoice'">
        <h3
          class="bold"
          :class="$vuetify.breakpoint.mdAndDown ? 'px-5' : 'px-2'"
          :style="$vuetify.breakpoint.mdAndDown ? 'font-size: 12px;' : ''"
        >
          <v-row dense>
            <v-col cols="8" md="10" class="text-right">
              {{
                $t(
                  "people:ShoppingBasket.ShoppingBasketTab.Total.text",
                  "Total",
                  "Label of the order table section"
                )
              }}:
            </v-col>
            <v-col cols="4" md="2" class="accent--text pr-3">{{
              totalAmountWithoutRefunds()
            }}</v-col>
          </v-row>
          <v-spacer class="my-1" />
          <v-row dense>
            <v-col
              cols="8"
              md="10"
              class="text-right"
              :style="$vuetify.breakpoint.mdAndDown ? 'font-size: 12px;' : ''"
            >
              {{
                $t(
                  "people:ShoppingBasket.ShoppingBasketTab.RefundAvailable.text",
                  "Refunds available",
                  "Label of the order table section"
                )
              }}:
            </v-col>
            <v-col cols="4" md="2" class="pr-3">{{
              masterCurrency(refundable)
            }}</v-col>
          </v-row>
        </h3>
      </template>

      <div class="px-1">
        <v-card color="white" class="mt-4 mb-2 py-4" tile>
          <v-row dense>
            <v-col cols="8" md="10" class="text-right">
              <h3
                :style="$vuetify.breakpoint.mdAndDown ? 'font-size: 14px;' : ''"
              >
                <template v-if="payable.entityType == 'Invoice'">
                  {{
                    $t(
                      "people:shoppingBasket.shoppingBasketTab.invoiceComponent.totalAmount.label",
                      "Total amount to be paid, incl all taxes",
                      "Label of the order table section"
                    )
                  }}:
                </template>
                <template v-else>
                  {{
                    $t(
                      "people:ShoppingBasket.ShoppingBasketTab.TotalAmount.text",
                      "Total amount to be paid incl VAT",
                      "Label of the order table section"
                    )
                  }}:
                </template>
              </h3>
            </v-col>
            <v-col cols="4" md="2" class="text-right">
              <h3
                class="accent--text mr-4"
                :style="$vuetify.breakpoint.mdAndDown ? 'font-size: 14px;' : ''"
                style="white-space: nowrap"
              >
                {{ selectedTotalAmount() }}
              </h3>
            </v-col>
          </v-row>
        </v-card>
      </div>
    </div>

    <ChangePayerDialog ref="changePayerDialog" />
  </div>
</template>
<script>
import gql from "fraql";
import { sortBy, flatten, uniq } from "lodash-es";
import { getFederationConfiguration } from "@composables/masterdata/config";
import { useCurrency } from "@composables/masterdata/config";
import { useProductCodenameHelpers } from "@composables/product_codename";
import { useDisciplineHelper } from "@composables/masterdata/competitions/disciplines";

import { useDisplayText } from "@composables/display_text";

import AppShowAthleteName from "@AppShows/person/AppShowAthleteName.vue";
import AppShowHorseName from "@AppShows/horse/AppShowHorseName.vue";

import HorseOrderDetails from "./OrderTableDetails/HorseOrderDetails.vue";
import HorseApplicationOrderDetails from "./OrderTableDetails/HorseApplicationOrderDetails.vue";
import OrganisationOrderDetails from "./OrderTableDetails/OrganisationOrderDetails.vue";
import EventProductOrderDetails from "./OrderTableDetails/EventProductOrderDetails.vue";
import EntryOrderDetails from "./OrderTableDetails/EntryOrderDetails.vue";
import EventOrderDetails from "./OrderTableDetails/EventOrderDetails.vue";
import GenericProductOrderDetails from "./OrderTableDetails/GenericProductOrderDetails.vue";
import InvoiceLineDetails from "./OrderTableDetails/InvoiceLineDetails.vue";

import ChangePayerDialog from "./ChangePayerDialog.vue";
import AppHorseSlot from "@elements/AppHorseSlot.vue";

export default {
  name: "OrderTable",
  components: {
    AppShowAthleteName,
    AppShowHorseName,
    ChangePayerDialog,
    AppHorseSlot
  },
  props: {
    shoppingBasketId: {
      type: String,
      default: null
    },
    payable: {
      type: Object,
      required: true
    },
    payableSections: {
      type: Array,
      required: true
    },
    refundable: {
      type: Number,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: true
    },
    entityType: {
      type: String,
      required: true
    },
    frozen: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const productCodenameHelpers = useProductCodenameHelpers();
    const { displayText } = useDisplayText();
    return {
      ...getFederationConfiguration(),
      ...useCurrency(),
      ...useDisciplineHelper(),
      ...productCodenameHelpers,
      displayText,
      sortBy
    };
  },
  data() {
    return {
      payableItemsSelectedStore: [],
      disabledGroups: [],
      mandatoryItemIds: []
    };
  },
  computed: {
    sortedSections: ({ payableSections: x }) => sortBy(x, ["rank"]),
    payableItemsSelected: {
      get() {
        return this.payableItemsSelectedStore;
      },
      set(v) {
        this.payableItemsSelectedStore = uniq([...v, ...this.mandatoryItemIds]);
        this.$emit("selectionUpdate", this.payableItemsSelectedStore);
      }
    },
    payableSectionSelected() {
      const sections = [];
      this.payableSections.forEach(section => {
        if (
          section.payableItems.some(
            payableItem =>
              this.payableItemsSelected.includes(payableItem.id) ||
              payableItem.mandatory
          )
        ) {
          sections.push(section.id);
        }
      });
      return sections;
    },
    allPayableItems() {
      return this.payableSections.map(x => x.payableItems).flat();
    }
  },
  beforeMount() {
    // disable group checkbox when none of the items can be paid
    this.payableSections.forEach((payableSection, sectionIndex) => {
      let cannotBePaidCount = 0;

      payableSection.payableItems.forEach(payableItem => {
        if (!this.payableItemCanBePaid(payableItem)) cannotBePaidCount += 1;
      });

      if (payableSection.payableItems.length == cannotBePaidCount) {
        this.disabledGroups.push(sectionIndex);
      }
    });
  },
  methods: {
    showDeadlineColumn(payableItems) {
      return payableItems.some(p => !!p.paymentDeadline);
    },
    addRemoveAddons(payableItemIds, payableItem) {
      if (!payableItem.payable.addonPayableIds?.length) return;
      if (payableItemIds.includes(payableItem.id)) {
        this.payableItemsSelected = this.payableItemsSelected.concat(
          payableItem.payable.addonPayableIds
        );
      } else {
        this.payableItemsSelected = this.payableItemsSelected.filter(x =>
          payableItem.payable.addonPayableIds.includes(x) ? false : true
        );
      }
    },
    cannotSelectItem(item) {
      return (
        !!item.payable?.addonForPayableId ||
        this.frozen ||
        this.mandatoryItemIds.includes(item.id) ||
        this.disabled ||
        !this.payableItemCanBePaid(item)
      );
    },
    sectionDisabled(payableSection, sectionIndex) {
      return (
        this.sectionIsOnlyFileldWithAddons(payableSection) ||
        this.payable.singlePaymentForWholeGroup ||
        this.frozen ||
        this.disabledGroup(payableSection, sectionIndex)
      );
    },
    sectionIsOnlyFileldWithAddons(payableSection) {
      return payableSection.payableItems.every(
        x => !!x.payable?.addonForPayableId
      );
    },
    disabledGroup(section, sectionIndex) {
      if (
        this.disabledGroups.includes(sectionIndex) ||
        this.mandatoryItemIds.length ||
        this.disabled
      ) {
        return true;
      } else {
        const itemsAllowed = section.payableItems.filter(item =>
          this.payableItemCanBePaid(item)
        );
        return itemsAllowed.length ? false : true;
      }
    },
    componentByEntityType(entity) {
      switch (entity) {
        case "Entry":
          return EntryOrderDetails;
        case "RegistrationEventProduct":
          return EventProductOrderDetails;
        case "Event":
          return EventOrderDetails;
        case "Organisation":
          return OrganisationOrderDetails;
        case "Horse":
          return HorseOrderDetails;
        case "HorseApplication":
          return HorseApplicationOrderDetails;
        case "Product":
          return GenericProductOrderDetails;
        case "InvoiceLine":
          return InvoiceLineDetails;
      }
    },
    removeItem(item, node) {
      const description = () => {
        const { payable } = item;
        if (item.type == "Horse") {
          return this.$t(
            "people:ShoppingBasket.RemoveHorseLicenceQuestion.text",
            "Are you sure you want to remove horse licence from shopping basket?",
            "Question text of Remove horse licence confirmation modal"
          );
        } else {
          return `<p>${this.$t(
            "people:ShoppingBasket.RemoveItemQuestion.text"
          )}</p>
          <div><strong>${this.$t(
            "common:product.codename." + payable.productCodename,
            payable.product ? payable.product.name : payable.productCodename
          )}</strong></div><br />
          ${(payable.addonPayables ?? [])
            .map(
              addonPayable => `
            <div><strong>${this.$t(
              "common:product.codename." + addonPayable.productCodename,
              addonPayable.product
                ? addonPayable.product.name
                : addonPayable.productCodename
            )}</strong></div>
            `
            )
            .join("<br />")}
          `;
        }
      };
      this.$root
        .$confirm(
          this.$t(
            "people:ShoppingBasket.RemoveItemQuestion.title",
            "Remove item",
            "Title of Remove item confirmation modal"
          ),
          description()
        )
        .then(async confirm => {
          if (confirm) {
            this.$mutateLoading.disableButton(node.target, "icon");
            this.$mutateLoading.showLoading(node.target);

            this.$apollo
              .mutate({
                mutation: gql`
                  mutation shoppingBasketCancelPayable(
                    $payableId: ID!
                    $shoppingBasketId: ID
                  ) {
                    shoppingBasketCancelPayable(
                      input: {
                        payableId: $payableId
                        shoppingBasketId: $shoppingBasketId
                      }
                    ) {
                      payable {
                        id
                      }
                    }
                  }
                `,
                variables: {
                  payableId: item.id,
                  shoppingBasketId: this.shoppingBasketId
                }
              })
              .then(() => {
                this.$emit("refetch");
              })
              .catch(error => {
                // Error
                this.$mutateLoading.hideLoading(node.target);
                this.$mutateLoading.enableButton(node.target, "icon");
                this.$root.$snackbarServerError(error, { level: "error" });
              });
          }
        });
    },
    unregister(item, evt) {
      this.$root
        .$confirm(
          this.$t(
            "people:ShoppingBasket.UnregisterQuestion.title",
            "Unregistration",
            "Title of Unregistration comfirmation modal in person shopping basket"
          ),
          this.$t(
            "people:ShoppingBasket.UnregisterQuestion.text",
            {
              athlete: this.showDisplayName(item.athletes[0]),
              horse: item.horses[0].isHorseSlot
                ? this.$t("horses:common.horseSlot.text", "Horse Slot")
                : item.horses[0].name
            },
            "Are you sure you want to unregister pair:<br><b>Athlete: {{athlete}}</b><br><b>Horse: {{horse}}</b>",
            "Question text of Remove item comfirmation modal"
          )
        )
        .then(resolve => {
          if (resolve === true) {
            this.$mutateLoading.disableButton(evt.target, "icon");
            this.$mutateLoading.showLoading(evt.target);
            this.$apollo
              .mutate({
                mutation: gql`
                  mutation registrationUnregister($id: ID) {
                    registrationUnregister(input: { id: $id }) {
                      errors
                    }
                  }
                `,
                variables: {
                  id: item.id
                }
              })
              .then(({ data }) => {
                if (data.errors && data.errors.length > 0) {
                  this.$mutateLoading.hideLoading(evt.target);
                  this.$mutateLoading.enableButton(evt.target, "icon");
                  this.$root.$snackbarServerError(data.errors, {
                    level: "error"
                  });
                } else {
                  this.$emit("refetch");
                }
              })
              .catch(error => {
                this.$mutateLoading.hideLoading(evt.target);
                this.$mutateLoading.enableButton(evt.target, "icon");
                this.$root.$snackbarServerError(error, { level: "error" });
              });
          }
        });
    },
    async _deletionItem(id) {
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation eventCancelPayableFromOrganiser($payableId: ID!) {
              eventCancelPayableFromOrganiser(
                input: { payableId: $payableId }
              ) {
                payable {
                  id
                }
              }
            }
          `,
          variables: { payableId: id }
        })
        .catch(error => {
          // Error
          this.$root.$snackbarServerError(error, { level: "error" });
          throw error;
        });

      return true;
    },
    cancelWalletFund(id, node) {
      this.$root
        .$confirm(
          this.$t(
            "people:ShoppingBasket.RemoveItemQuestion.title",
            "Remove item",
            "Title of Remove item comfirmation modal"
          ),
          this.$t(
            "people:ShoppingBasket.RemoveItemAddFundsQuestion.text",
            "Are you sure you want to cancel the request to add funds to event wallet?",
            "Question text of Remove item comfirmation modal"
          )
        )
        .then(async confirm => {
          if (confirm) {
            this.$mutateLoading.disableButton(node.target, "icon");
            this.$mutateLoading.showLoading(node.target);

            await this._deletionItem(id);

            this.$emit("refetch");
            /*
            this.$mutateLoading.hideLoading(node.target);
            this.$mutateLoading.enableButton(node.target, "icon");
            */
          }
        });
    },
    async cancelWalletFunds(section, node) {
      this.$root
        .$confirm(
          this.$t(
            "people:ShoppingBasket.RemoveItemQuestion.title",
            "Remove item",
            "Title of Remove item comfirmation modal"
          ),
          this.$t(
            "people:ShoppingBasket.RemoveItemAddFundsQuestion.text",
            "Are you sure you want to cancel the request to add funds to event wallet?",
            "Question text of Remove Add funds item comfirmation modal"
          )
        )
        .then(async confirm => {
          if (confirm) {
            this.$mutateLoading.disableButton(node.target, "icon");
            this.$mutateLoading.showLoading(node.target);
            const promises = [];
            section.payableItems.forEach(async item => {
              if (
                this.$allowedMutation(
                  item.payable.allowedMutations,
                  "eventCancelPayableFromOrganiser"
                )
              ) {
                promises.push(this._deletionItem(item.id));
              }
            });

            await Promise.all(promises);

            this.$emit("refetch");
            this.$mutateLoading.hideLoading(node.target);
            this.$mutateLoading.enableButton(node.target, "icon");
          }
        });
    },
    preselect(payableSections) {
      let preselections = [];

      payableSections.forEach(({ payableItems }) => {
        payableItems.forEach(item => {
          if (item.mandatory == true && this.payableItemCanBePaid(item)) {
            this.mandatoryItemIds.push(item.id);
            preselections.push(item.id);
          } else if (this.payableItemCanBePaid(item))
            preselections.push(item.id);
        });
      });

      this.payableItemsSelected = preselections;
    },
    payableItemCanBePaid(item) {
      if (item.paymentInProcess === true) return false; // cannot be paid when payment in progress
      if (item.paymentNotAllowedReasons?.length > 0) return false; // cannot be paid when there are "payment not allowed" reasons
      return true;
    },
    updateSectionSelection(payableSectionIds, payableSection) {
      let payableItemIds = [];

      if (payableSectionIds.includes(payableSection.id)) {
        // Add payableSection.payableItems ids thats not mandatory and payable
        payableItemIds = uniq([
          ...payableSection.payableItems
            .filter(
              payableItem =>
                !payableItem.mandatory && this.payableItemCanBePaid(payableItem)
            )
            .map(x => x.id),
          ...this.payableItemsSelected
        ]);

        payableSection.payableItems.forEach(payableItem => {
          if (!payableItem.payable.addonPayableIds?.length) return;
          payableItemIds = payableItemIds.concat(
            payableItem.payable.addonPayableIds
          );
        });
      } else {
        // Remove payableSection.payableItems ids
        payableItemIds = payableSection.payableItems.map(
          payableItem => payableItem.id
        );
        payableItemIds = this.payableItemsSelected.filter(y =>
          payableItemIds.includes(y) ? false : true
        );

        payableSection.payableItems.forEach(payableItem => {
          if (!payableItem.payable.addonPayableIds?.length) return;
          payableItemIds = payableItemIds.filter(payableItemId =>
            payableItem.payable.addonPayableIds.includes(payableItemId)
              ? false
              : true
          );
        });
      }

      this.payableItemsSelected = payableItemIds;
    },
    bookingStatus(registrationProduct) {
      if (registrationProduct && registrationProduct.eventProduct) {
        switch (registrationProduct.eventProduct) {
          case "fully_during_competition_free_booking":
            return "booked";
          case "full_price_online_no_refund":
            return this.masterCurrency(
              registrationProduct.eventProduct.priceCents *
                registrationProduct.quantity
            );
          case "full_price_online_booking_nonrefundable":
            return this.masterCurrency(
              registrationProduct.eventProduct.priceCents *
                registrationProduct.quantity
            );
        }
      } else {
        return "";
      }
    },
    totalAmountWithoutRefunds() {
      let pricesInCents = [];
      this.payableSections.forEach(payableSection => {
        payableSection.payableItems.forEach(item => {
          if (this.payableItemsSelected.includes(item.id)) {
            pricesInCents.push(item.amountCents);
          }
        });
      });

      let total = 0;
      pricesInCents.forEach(amountCents => {
        total += amountCents;
      });
      return this.masterCurrency(total);
    },
    selectedTotalAmount() {
      let pricesInCents = [];
      this.payableSections.forEach(payableSection => {
        payableSection.payableItems.forEach(item => {
          if (this.payableItemsSelected.includes(item.id)) {
            pricesInCents.push(item.amountCents);
          }
        });
      });

      let total = 0;
      pricesInCents.forEach(amountCents => {
        total += amountCents;
      });
      total -= this.refundable;
      return this.masterCurrency(total < 0 ? 0 : total);
    },
    sectionTitle(section) {
      if (this.entityType == "Event") {
        if (section.entityType == "Registration") {
          return this.$t(
            "people:ShoppingBasket.ShoppingBasketTab.Registration.text",
            "Registrations",
            "Text of the order table section"
          );
        } else if (section.entityType == "Event") {
          return this.$t(
            "people:ShoppingBasket.ShoppingBasketTab.TopupToEventWallet.text",
            "Top-up to event wallet from organizer",
            "Text of the order table section"
          );
        }
      } else if (this.entityType == "Federation") {
        if (section.entityType == "Product") {
          // From mixin productCodename
          if (section.product.codename)
            return this.productCodenameToName(section.product.codename);
          else return this.productSystemTagToName(section.product.systemTags);
        }
      } else if (this.entityType == "Organisation") {
        return section.product.name;
      }
    },
    returnPayableItemMutations(section) {
      return flatten(section.payableItems.map(x => x.payable.allowedMutations));
    },

    openPayerDialog(registration) {
      this.$refs.changePayerDialog.openForm(registration);
    }
  },
  fragments: {
    payabeSectionProductFields: gql`
      fragment _ on Product {
        id
        name
        codename
        systemTags
        relatedEntities
      }
    `,
    payableFields: gql`
      fragment _ on Payable {
        id
        allowedMutations

        year
        productCodename
        productParams
        productId
        product @client {
          id
          name
          relatedEntities
        }

        ${GenericProductOrderDetails.fragments.payableFields}

        addonForPayableId
        addonForPayable {
          id
          productCodename
          productId
          product @client {
            id
            name
            relatedEntities
          }
        }

        registrationEventProduct {
          id
          entry {
            id
            competition {
              id
              ${EventProductOrderDetails.fragments.competitionFields}
            }
          }
        }

        addonPayableIds
        addonPayables {
          id
          productCodename
          productId
          product @client {
            id
            name
            relatedEntities
          }
        }
      }
    `,
    personPayableGroupFields: gql`
      fragment _ on PersonPayableGroup {
        singlePaymentForWholeGroup
      }
    `,
    invoiceLineFields: gql`
      fragment _ on InvoiceLine {
        ${InvoiceLineDetails.fragments.invoiceLineFields}
      }
    `,
    entryFields: GenericProductOrderDetails.fragments.entryFields,
    registrationFields: gql`
      fragment _ on Registration {
        eventId
        ${ChangePayerDialog.fragments.registrationFields}
        horses {
          id
          ${AppHorseSlot.fragments.horseFields}
        }
      }
    `
  }
};
</script>
<style lang="scss">
.payable-section-checkbox-trickery {
  position: relative;
  bottom: 7px;
}
.fipa-app .fipa--header.fipa--header-registration {
  height: auto !important;
  flex-direction: column;
}
.full-height {
  height: 100%;
}
@media (max-width: 920px) {
  .entsys--table {
    tbody tr {
      td:first-child {
        padding-left: 20px;
      }
      td:last-child {
        padding-right: 20px;
      }
    }
  }
}
.order-content {
  margin-bottom: 14px;
}
.straight-line {
  text-overflow: ellipsis;
  width: 110px;
  white-space: nowrap;
  overflow: hidden;
}
.reg-btn {
  align-self: baseline;
  label {
    top: 2px;
  }
}
</style>
